/**
 * IndexedDB layer — the source of truth (§3.1).
 * Every committed write bumps rev, sets updatedAt, and appends to the oplog
 * in the same transaction. Snapshots are debounced (Gate 1 amendment 1).
 */
import { openDB, type DBSchema, type IDBPDatabase, type StoreNames } from 'idb';
import { newId, nowIso } from './ids';
import { announceChange } from './bus';
import type {
  AiCall, Client, DocumentRecord, Envelope, InventoryItem, Job, OplogRow,
  OutboxItem, Photo, PriceItem, ReviewItem, Settings, Shape, SnapshotRow, TakeoffLine,
} from './types';
import { SCHEMA_VERSION } from './types';

export interface FgbsDB extends DBSchema {
  clients:        { key: string; value: Client;         indexes: { updatedAt: string } };
  jobs:           { key: string; value: Job;            indexes: { status: string; updatedAt: string } };
  shapes:         { key: string; value: Shape;          indexes: { jobId: string } };
  takeoffLines:   { key: string; value: TakeoffLine;    indexes: { jobId: string } };
  inventoryItems: { key: string; value: InventoryItem;  indexes: { jobId: string } };
  priceBook:      { key: string; value: PriceItem;      indexes: { itemId: string } };
  documents:      { key: string; value: DocumentRecord; indexes: { jobId: string } };
  photos:         { key: string; value: Photo;          indexes: { jobId: string } };
  reviewQueue:    { key: string; value: ReviewItem;     indexes: { status: string } };
  aiCalls:        { key: string; value: AiCall };
  outbox:         { key: string; value: OutboxItem;     indexes: { state: string } };
  oplog:          { key: number; value: OplogRow };
  snapshots:      { key: string; value: SnapshotRow };
  meta:           { key: string; value: unknown };
}

export type RecordStore =
  | 'clients' | 'jobs' | 'shapes' | 'takeoffLines' | 'inventoryItems'
  | 'priceBook' | 'documents' | 'photos' | 'reviewQueue' | 'aiCalls' | 'outbox';

export const RECORD_STORES: RecordStore[] = [
  'clients', 'jobs', 'shapes', 'takeoffLines', 'inventoryItems',
  'priceBook', 'documents', 'photos', 'reviewQueue', 'aiCalls', 'outbox',
];

let dbp: Promise<IDBPDatabase<FgbsDB>> | null = null;
let dbName = 'fgbs';

/** test hook: isolate databases per test */
export function _setDbName(name: string): void {
  dbName = name;
  dbp = null;
}

export function getDb(): Promise<IDBPDatabase<FgbsDB>> {
  dbp ??= openDB<FgbsDB>(dbName, 1, {
    upgrade(db) {
      const withUpdated = (s: 'clients' | 'jobs') =>
        db.createObjectStore(s, { keyPath: 'id' }).createIndex('updatedAt', 'updatedAt');
      withUpdated('clients');
      const jobs = db.createObjectStore('jobs', { keyPath: 'id' });
      jobs.createIndex('status', 'status');
      jobs.createIndex('updatedAt', 'updatedAt');
      for (const s of ['shapes', 'takeoffLines', 'inventoryItems', 'documents', 'photos'] as const) {
        db.createObjectStore(s, { keyPath: 'id' }).createIndex('jobId', 'jobId');
      }
      db.createObjectStore('priceBook', { keyPath: 'id' }).createIndex('itemId', 'itemId');
      db.createObjectStore('reviewQueue', { keyPath: 'id' }).createIndex('status', 'status');
      db.createObjectStore('aiCalls', { keyPath: 'id' });
      db.createObjectStore('outbox', { keyPath: 'id' }).createIndex('state', 'state');
      db.createObjectStore('oplog', { autoIncrement: true });
      db.createObjectStore('snapshots', { keyPath: 'at' });
      db.createObjectStore('meta');
    },
  });
  return dbp;
}

// ---------- envelope lifecycle ----------

type Bare<T extends Envelope> = Omit<T, keyof Envelope> & Partial<Pick<Envelope, 'id' | 'owner'>>;

export async function createRecord<S extends RecordStore>(
  store: S, data: Bare<FgbsDB[S]['value']>, opts?: { snapshot?: boolean },
): Promise<FgbsDB[S]['value']> {
  const now = nowIso();
  const rec = {
    ...data,
    id: data.id ?? newId(),
    owner: data.owner ?? (await getSettings()).currentUser,
    createdAt: now, updatedAt: now, rev: 1, deletedAt: null,
  } as FgbsDB[S]['value'];
  await writeWithLog(store, rec);
  if (opts?.snapshot !== false) scheduleSnapshot();
  return rec;
}

export async function putRecord<S extends RecordStore>(
  store: S, rec: FgbsDB[S]['value'], opts?: { snapshot?: boolean },
): Promise<FgbsDB[S]['value']> {
  const next = { ...rec, rev: rec.rev + 1, updatedAt: nowIso() };
  await writeWithLog(store, next);
  if (opts?.snapshot !== false) scheduleSnapshot();
  return next;
}

export async function softDelete<S extends RecordStore>(store: S, id: string): Promise<void> {
  const db = await getDb();
  const rec = await db.get(store, id);
  if (!rec || rec.deletedAt) return;
  await putRecord(store, { ...rec, deletedAt: nowIso() } as FgbsDB[S]['value']);
}

/** bulk create in ONE transaction (oplog included) — the §7 import budget needs this */
export async function createRecords<S extends RecordStore>(
  store: S, datas: Bare<FgbsDB[S]['value']>[], opts?: { snapshot?: boolean },
): Promise<FgbsDB[S]['value'][]> {
  if (datas.length === 0) return [];
  const now = nowIso();
  const owner = (await getSettings()).currentUser;
  const recs = datas.map((d) => ({
    ...d, id: d.id ?? newId(), owner: d.owner ?? owner,
    createdAt: now, updatedAt: now, rev: 1, deletedAt: null,
  } as FgbsDB[S]['value']));
  const db = await getDb();
  const tx = db.transaction([store, 'oplog'] as StoreNames<FgbsDB>[], 'readwrite');
  const st = tx.objectStore(store);
  const ol = tx.objectStore('oplog');
  const errs: unknown[] = [];
  const ops: Promise<unknown>[] = [];
  try {
    for (const rec of recs) {
      ops.push((st.put(rec as never) as Promise<unknown>).catch((e) => { errs.push(e); }));
    }
    // one oplog marker for the whole batch: the records carry provenance and rev;
    // per-row oplog entries double the transaction size for no audit gain
    ops.push((ol.add({ recordType: store, recordId: `bulk:${recs.length}`, rev: 1, at: now } as never) as Promise<unknown>).catch((e) => { errs.push(e); }));
  } catch (e) {
    // a synchronous platform throw (e.g. quota) must not let earlier puts auto-commit
    try { tx.abort(); } catch { /* already aborting */ }
    throw e;
  }
  try {
    await tx.done;
  } catch (e) {
    await Promise.all(ops);
    throw errs[0] ?? e; // the request-level error (e.g. QuotaExceededError), not the bare abort
  }
  if (opts?.snapshot !== false) scheduleSnapshot();
  return recs;
}

/** plain language for storage failures — shown verbatim to David */
export function explainStorageError(e: unknown): string | null {
  const name = (e as { name?: string })?.name ?? '';
  const msg = String((e as Error)?.message ?? e);
  if (name === 'QuotaExceededError' || /quota/i.test(msg)) {
    return 'This device’s storage for the app is full. Nothing was changed. Export a backup from the Backup page, then delete old photos or jobs to free space — your existing data is safe.';
  }
  return null;
}

async function writeWithLog<S extends RecordStore>(store: S, rec: FgbsDB[S]['value']): Promise<void> {
  const db = await getDb();
  const tx = db.transaction([store, 'oplog'] as StoreNames<FgbsDB>[], 'readwrite');
  const errs: unknown[] = [];
  const ops = [
    (tx.objectStore(store).put(rec as never) as Promise<unknown>).catch((e) => { errs.push(e); }),
    (tx.objectStore('oplog').add({
      recordType: store, recordId: rec.id, rev: rec.rev, at: rec.updatedAt,
    } as never) as Promise<unknown>).catch((e) => { errs.push(e); }),
  ];
  try {
    await tx.done;
  } catch (e) {
    await Promise.all(ops);
    throw errs[0] ?? e;
  }
  announceChange();   // other tabs are showing the version from before this
}

export async function getRecord<S extends RecordStore>(store: S, id: string): Promise<FgbsDB[S]['value'] | undefined> {
  return (await getDb()).get(store, id);
}

/** live = tombstones excluded */
export async function listLive<S extends RecordStore>(store: S): Promise<FgbsDB[S]['value'][]> {
  return (await (await getDb()).getAll(store)).filter((r) => !r.deletedAt);
}

export async function listByJob<S extends 'shapes' | 'takeoffLines' | 'inventoryItems' | 'documents' | 'photos'>(
  store: S, jobId: string,
): Promise<FgbsDB[S]['value'][]> {
  const rows = await (await getDb()).getAllFromIndex(store, 'jobId', IDBKeyRange.only(jobId));
  return rows.filter((r) => !r.deletedAt);
}

/** The same rows plus the tombstoned ones. Only the "show removed" views want
 *  these, so they are fetched on request rather than carried in every load. */
export async function listByJobIncludingRemoved<S extends 'shapes' | 'takeoffLines' | 'inventoryItems' | 'documents' | 'photos'>(
  store: S, jobId: string,
): Promise<FgbsDB[S]['value'][]> {
  return (await getDb()).getAllFromIndex(store, 'jobId', IDBKeyRange.only(jobId));
}

// ---------- settings ----------

export function defaultSettings(): Settings {
  return {
    company: {
      name: 'Forever Green Landscape and Designs, LLC',
      owner: 'David Payan', phone: '', email: '', roc: '341624',
    },
    currentUser: 'david',
    defaults: { markupPct: 0, taxPct: 0, depositPct: 25, validityDays: 30 },
    basemap: 'mc2026',   // the county's current flight; a nationwide layer sits under it
    termsText: '',
    proxyUrl: '', proxyPassphrase: '',
    autoExport: false, lastExportAt: null, lastSnapshotAt: null,
    bidSeq: 1004,
    settingsRev: 2,
  };
}

export const SETTINGS_REV = 2;

/**
 * Re-default a stored preference that has been shown to be wrong.
 *
 * rev 2: the imagery choice. USGS was the original default and is stored in
 * every install made before 2026-07-25. Over Surprise that service holds no
 * tile deeper than level 16, so at working zoom it renders a 32x blow-up of a
 * blurry photo — which is exactly what the field reported after redeploying.
 * A new default in defaultSettings() only reaches new installs, so existing
 * ones get moved once, here. Anything chosen after this migration is kept.
 */
export function migrateSettings(s: Settings): Settings {
  if ((s.settingsRev ?? 0) >= SETTINGS_REV) return s;
  return { ...s, basemap: defaultSettings().basemap, settingsRev: SETTINGS_REV };
}

export async function getSettings(): Promise<Settings> {
  const db = await getDb();
  const stored = (await db.get('meta', 'settings')) as Settings | undefined;
  if (!stored) return { ...defaultSettings(), settingsRev: SETTINGS_REV };
  const migrated = migrateSettings(stored);
  if (migrated !== stored) await db.put('meta', migrated, 'settings');
  return migrated;
}

/**
 * Fields the app maintains for itself. A screen may not write these back.
 *
 * Every settings screen holds a copy of the record from the moment it was
 * opened. Saving writes that whole copy back. If a counter rides along in the
 * same record, anything that advanced it in the meantime is undone — and the
 * bid counter advancing is exactly what happens when someone makes a job in
 * another tab, or in the same tab before returning to Settings.
 */
const MACHINE_OWNED = ['bidSeq', 'lastExportAt', 'lastSnapshotAt'] as const;

/**
 * Save the preferences a person edited.
 *
 * Machine-owned fields are taken from what is stored, never from the caller,
 * so a save can only ever move the things the screen actually shows. Internal
 * writers that legitimately move a counter use putSettingsRaw.
 */
export async function putSettings(s: Settings): Promise<void> {
  const db = await getDb();
  // One transaction, not a read then a write. Two round trips gave a page
  // reload a window to land in between, and a save typed on the Settings
  // screen was lost outright — caught by the suite 2026-07-27 after this
  // function grew its second step.
  const tx = db.transaction('meta', 'readwrite');
  const store = tx.objectStore('meta');
  const stored = (await store.get('settings')) as Settings | undefined;
  const merged: Settings = { ...s };
  if (stored) {
    for (const k of MACHINE_OWNED) {
      (merged as unknown as Record<string, unknown>)[k] = stored[k];
    }
  }
  await store.put(merged, 'settings');
  await tx.done;
  announceChange();
  scheduleSnapshot();
}

/** Write the settings record verbatim. For restore and for the counters. */
export async function putSettingsRaw(s: Settings): Promise<void> {
  const db = await getDb();
  await db.put('meta', s, 'settings');
  announceChange();
  scheduleSnapshot();
}

/** "B-1004" -> 1004. Anything else -> 0, so imported free text cannot poison
 *  the counter by parsing as a huge number. */
export function bidSeqOf(bidNumber: unknown): number {
  const m = /^B-0*(\d{1,9})$/.exec(String(bidNumber ?? '').trim());
  return m ? Number(m[1]) : 0;
}

/**
 * The next bid number that is free.
 *
 * The stored counter is a hint, not the authority. The authority is every
 * number already used, including on removed jobs: removal is a tombstone, and
 * a removed job's number may already be printed on a proposal in a board's
 * hands. Taking the max of both means a counter that has been rolled back —
 * by a stale save, a second tab, a restore, or an import — cannot hand out a
 * number twice.
 *
 * Found live 2026-07-27 on Phil's browser: B-1004 and B-1005 each sat on two
 * different jobs, and the stored counter read 1006 while B-1007 was already
 * assigned, so the next job created would have collided again on the first
 * click. The counter had been reset to its 1004 default at some point between
 * 00:38 and 01:03; which write did it was never established, which is the
 * reason this guards the invariant instead of one suspected path.
 */
export function nextBidSeq(storedSeq: number, used: readonly unknown[]): number {
  let highest = 0;
  for (const b of used) { const n = bidSeqOf(b); if (n > highest) highest = n; }
  return Math.max(storedSeq, highest + 1);
}

/**
 * Reserve the next bid number.
 *
 * One readwrite transaction over jobs, documents and meta, so two tabs
 * serialise instead of both reading the same counter and both believing it.
 */
export async function nextBidNumber(): Promise<string> {
  const db = await getDb();
  const tx = db.transaction(['jobs', 'documents', 'meta'], 'readwrite');
  const jobs = await tx.objectStore('jobs').getAll();
  const docs = await tx.objectStore('documents').getAll();
  const stored = (await tx.objectStore('meta').get('settings')) as Settings | undefined;
  const base = migrateSettings(stored ?? { ...defaultSettings(), settingsRev: SETTINGS_REV });
  // a document's own number is "B-1004-P1"; the bid it belongs to is frozen
  // inside its payload, and that payload is what a board is holding
  const used = [...jobs.map((j) => j.bidNumber), ...docs.map((d) => d.payload?.bidNumber)];
  const n = nextBidSeq(base.bidSeq, used);
  await tx.objectStore('meta').put({ ...base, bidSeq: n + 1 }, 'settings');
  await tx.done;
  announceChange();
  scheduleSnapshot();
  return `B-${n}`;
}

// ---------- snapshots (§3.2 backup layer 1) ----------

const SNAPSHOT_DEBOUNCE_MS = 5000;
let snapTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleSnapshot(): void {
  if (snapTimer) clearTimeout(snapTimer);
  snapTimer = setTimeout(() => { void snapshotNow('debounce'); }, SNAPSHOT_DEBOUNCE_MS);
}

/** e2e/test hook and issue-time hook */
export async function snapshotNow(trigger: SnapshotRow['trigger']): Promise<SnapshotRow> {
  if (snapTimer) { clearTimeout(snapTimer); snapTimer = null; }
  const bundle = await collectBundle({ includePhotoBytes: false });
  const json = JSON.stringify(bundle);
  const gz = await gzip(json);
  const row: SnapshotRow = {
    at: nowIso(), trigger, schemaVersion: SCHEMA_VERSION, bytes: gz.byteLength, gz,
  };
  const db = await getDb();
  await db.put('snapshots', row);
  const all = await db.getAll('snapshots');
  const keep = new Set(pruneSnapshots(all.map((s) => s.at), row.at));
  for (const s of all) if (!keep.has(s.at)) await db.delete('snapshots', s.at);
  const settings = await getSettings();
  await db.put('meta', { ...settings, lastSnapshotAt: row.at }, 'settings');
  return row;
}

/**
 * Retention (pure, tested): newest 12, then one per day for 14 days,
 * then one per week for 8 weeks. Returns the timestamps to keep.
 */
export function pruneSnapshots(timestamps: string[], nowTs: string): string[] {
  const sorted = [...timestamps].sort().reverse(); // newest first
  const keep = new Set<string>(sorted.slice(0, 12));
  const now = Date.parse(nowTs);
  const dayKeys = new Set<string>(); const weekKeys = new Set<string>();
  for (const t of sorted) {
    const age = now - Date.parse(t);
    const d = Math.floor(age / 86400000);
    if (d <= 14) {
      const key = t.slice(0, 10);
      if (!dayKeys.has(key)) { dayKeys.add(key); keep.add(t); }
    } else if (d <= 56) {
      const key = String(Math.floor(d / 7));
      if (!weekKeys.has(key)) { weekKeys.add(key); keep.add(t); }
    }
  }
  return [...keep];
}

// ---------- full-state bundle (shared by snapshots and export §3.2) ----------

export interface Bundle {
  app: 'forever-green-bid-studio';
  schema: number;
  exportedAt: string;
  settings: Settings;
  data: Record<string, unknown[]>;
  photoBytes?: { id: string; mime: string; b64: string }[];
}

export async function collectBundle(opts: { includePhotoBytes: boolean }): Promise<Bundle> {
  const db = await getDb();
  const data: Record<string, unknown[]> = {};
  for (const store of RECORD_STORES) {
    const rows = await db.getAll(store);
    data[store] = store === 'photos'
      ? (rows as Photo[]).map(({ bytes: _bytes, ...meta }) => meta)
      : rows;
  }
  const bundle: Bundle = {
    app: 'forever-green-bid-studio', schema: SCHEMA_VERSION, exportedAt: nowIso(),
    settings: await getSettings(), data,
  };
  if (opts.includePhotoBytes) {
    const photos = (await db.getAll('photos')) as Photo[];
    bundle.photoBytes = photos.map((p) => ({ id: p.id, mime: p.mime, b64: bufToB64(p.bytes) }));
  }
  return bundle;
}

/** transactional restore: wipes record stores and writes the bundle, or throws untouched */
export async function restoreBundle(bundle: Bundle): Promise<void> {
  const db = await getDb();
  const stores = [...RECORD_STORES, 'oplog'] as StoreNames<FgbsDB>[];
  const tx = db.transaction(stores, 'readwrite');
  for (const s of stores) await tx.objectStore(s).clear();
  const photoBytes = new Map((bundle.photoBytes ?? []).map((p) => [p.id, p]));
  for (const store of RECORD_STORES) {
    const rows = (bundle.data[store] ?? []) as Envelope[];
    for (const row of rows) {
      const value = store === 'photos'
        ? { ...row, bytes: b64ToBuf(photoBytes.get(row.id)?.b64 ?? ''), mime: photoBytes.get(row.id)?.mime ?? 'image/jpeg' }
        : row;
      await tx.objectStore(store).put(value as never);
      await tx.objectStore('oplog').add({
        recordType: store, recordId: row.id, rev: row.rev, at: nowIso(),
      } as never);
    }
  }
  await tx.done;
  // a restore replaces the record wholesale, counters included — the bid
  // counter that came with the backup belongs to the jobs that came with it
  await putSettingsRaw(bundle.settings);
  await snapshotNow('import');
}

// ---------- small codecs ----------

export async function gzip(s: string): Promise<ArrayBuffer> {
  const stream = new Blob([s]).stream().pipeThrough(new CompressionStream('gzip'));
  return await new Response(stream).arrayBuffer();
}

export async function gunzip(buf: ArrayBuffer): Promise<string> {
  const stream = new Blob([buf]).stream().pipeThrough(new DecompressionStream('gzip'));
  return await new Response(stream).text();
}

export function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
